import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

type NoticeVariant = 'error' | 'warning';
type Notice = { message: string; variant: NoticeVariant } | null;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const mapSignInError = (err: unknown): Notice => {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return { variant: 'error', message: 'Invalid email or password.' };
    case 'auth/invalid-email':
      return { variant: 'error', message: 'That email address looks invalid.' };
    case 'auth/user-disabled':
      return { variant: 'error', message: 'This account has been disabled. Contact a platform administrator.' };
    case 'auth/too-many-requests':
      return { variant: 'error', message: 'Too many failed attempts. Please wait a few minutes or reset your password.' };
    case 'auth/network-request-failed':
      return { variant: 'error', message: 'Network error — check your connection and try again.' };
    case 'admin/not-authorized':
      return { variant: 'warning', message: 'Your credentials are valid, but this account is not authorized to access the admin panel. Contact a platform administrator.' };
    default:
      return { variant: 'error', message: 'We could not sign you in. Please try again in a moment.' };
  }
};

const mapGoogleSignInError = (err: unknown): Notice => {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return null;
    case 'auth/popup-blocked':
      return { variant: 'error', message: 'Your browser blocked the Google sign-in popup. Allow popups for this site and try again.' };
    case 'auth/account-exists-with-different-credential':
      return { variant: 'error', message: 'An account with this email already exists using a different sign-in method.' };
    case 'auth/network-request-failed':
      return { variant: 'error', message: 'Network error — check your connection and try again.' };
    case 'admin/not-authorized':
      return { variant: 'warning', message: 'Your Google account is not authorized to access the admin panel. Contact a platform administrator.' };
    default:
      return { variant: 'error', message: 'Google sign-in failed. Please try again.' };
  }
};

const mapResetError = (err: unknown): Notice => {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-email':
      return { variant: 'error', message: 'That email address looks invalid.' };
    case 'auth/too-many-requests':
      return { variant: 'error', message: 'Too many requests. Please wait a few minutes before trying again.' };
    case 'auth/network-request-failed':
      return { variant: 'error', message: 'Network error — check your connection and try again.' };
    default:
      return { variant: 'error', message: 'Could not send the reset email. Please try again.' };
  }
};

const LoginPage: React.FC = () => {
  const { signInWithGoogle, signInWithEmail, resetPassword, loading } = useAuth();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [notice, setNotice] = useState<Notice>(null);
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Visibility toggle for the password field. Resets to hidden on unmount
  // (i.e. when leaving the page), since the component state is re-initialised.
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setNotice(null);
    setSuccess('');
  };

  const handleGoogleSignIn = async () => {
    setNotice(null);
    setSuccess('');
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const n = mapGoogleSignInError(err);
      if (n) setNotice(n);
      if (!n || (err as { code?: string })?.code === undefined) {
        console.error('[LoginPage] Google sign-in failed:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    setSuccess('');

    const email = formData.email.trim();
    if (!email || !formData.password) {
      setNotice({ variant: 'error', message: 'Please enter both email and password.' });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setNotice({ variant: 'error', message: 'That email address looks invalid.' });
      return;
    }

    setIsSubmitting(true);
    try {
      await signInWithEmail(email, formData.password);
    } catch (err) {
      const n = mapSignInError(err);
      setNotice(n);
      // Clear the password after a failed attempt so the stale value isn't left in the field
      setFormData((prev) => ({ ...prev, password: '' }));
      const code = (err as { code?: string })?.code;
      if (!code || code === 'auth/internal-error') {
        console.error('[LoginPage] Email sign-in failed:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    setSuccess('');

    const email = formData.email.trim();
    if (!email) {
      setNotice({ variant: 'error', message: 'Please enter your email address.' });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setNotice({ variant: 'error', message: 'That email address looks invalid.' });
      return;
    }

    const genericSuccess = 'If an account exists for that email, a password reset link has been sent.';
    setIsSubmitting(true);
    try {
      await resetPassword(email);
      setSuccess(genericSuccess);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      // Mask account-existence signals — always surface the generic success.
      if (code === 'auth/user-not-found' || code === 'auth/missing-email') {
        setSuccess(genericSuccess);
      } else {
        setNotice(mapResetError(err));
        if (!code) console.error('[LoginPage] Password reset failed:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const NoticeBanner: React.FC<{ notice: Notice }> = ({ notice }) => {
    if (!notice) return null;
    const isWarning = notice.variant === 'warning';
    const wrapper = isWarning
      ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20'
      : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20';
    const iconColor = isWarning ? 'text-amber-500' : 'text-red-500';
    const textColor = isWarning ? 'text-amber-800 dark:text-amber-400' : 'text-red-800 dark:text-red-400';
    return (
      <div role="alert" aria-live="assertive" className={`mb-6 border rounded-xl p-4 flex items-start gap-3 ${wrapper}`}>
        {isWarning ? (
          <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.987-1.743 2.987H3.482c-1.53 0-2.492-1.653-1.743-2.987l6.518-11.59zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )}
        <p className={`text-sm font-['Manrope'] ${textColor}`}>{notice.message}</p>
      </div>
    );
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center px-4 py-12 transition-colors duration-200">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="/logo_black.png"
              alt="Virtual Client Logo"
              className="w-48 h-24 sm:w-64 sm:h-32 object-contain mx-auto mb-6 dark:hidden"
            />
            <img
              src="/logo.png"
              alt="Virtual Client Logo"
              className="w-48 h-24 sm:w-64 sm:h-32 object-contain mx-auto mb-6 hidden dark:block"
            />
            <h1 className="text-gray-900 dark:text-white text-2xl sm:text-3xl font-bold font-['Poppins'] mb-2">
              Reset Password
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-base font-['Manrope']">
              Enter your email to receive a password reset link
            </p>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 sm:p-8">
            <NoticeBanner notice={notice} />

            {success && (
              <div role="alert" aria-live="polite" className="mb-6 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl p-4 flex items-start gap-3">
                <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-emerald-800 dark:text-emerald-400 text-sm font-['Manrope']">{success}</p>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-['Manrope']">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-600 focus:border-transparent transition-all font-['Manrope']"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                className="w-full bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 font-semibold font-['Manrope'] py-3.5 rounded-lg transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </button>

              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium font-['Manrope'] py-2 transition-all duration-200"
              >
                Back to Sign In
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-['Manrope']">
              © {new Date().getFullYear()} Virtual Client. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center px-4 py-12 transition-colors duration-200">
      <div className="w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <img
            src="/logo_black.png"
            alt="Virtual Client Logo"
            className="w-48 h-24 sm:w-64 sm:h-32 object-contain mx-auto mb-6 dark:hidden"
          />
          <img
            src="/logo.png"
            alt="Virtual Client Logo"
            className="w-48 h-24 sm:w-64 sm:h-32 object-contain mx-auto mb-6 hidden dark:block"
          />
          <h1 className="text-gray-900 dark:text-white text-2xl sm:text-3xl font-bold font-['Poppins'] mb-2">
            SuperAdmin Panel
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-base font-['Manrope']">
            Sign in with your email/password or Google account
          </p>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 sm:p-8">
          <NoticeBanner notice={notice} />

          {/* Email Sign In Form */}
          <form onSubmit={handleEmailSignIn} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-['Manrope']">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-600 focus:border-transparent transition-all font-['Manrope']"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-['Manrope']">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-11 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-600 focus:border-transparent transition-all font-['Manrope']"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors focus:outline-none"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium font-['Manrope'] transition-colors"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || loading}
              aria-busy={isSubmitting}
              className="w-full bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 font-semibold font-['Manrope'] py-3.5 rounded-lg transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-gray-300 dark:border-gray-800"></div>
            <span className="px-4 text-sm text-gray-500 dark:text-gray-400 font-['Manrope']">or</span>
            <div className="flex-1 border-t border-gray-300 dark:border-gray-800"></div>
          </div>

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading || isSubmitting}
            className="w-full bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 font-semibold font-['Manrope'] py-3.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </button>

          {/* Footer Note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-['Manrope']">
              Only authorized administrators can access this panel
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-['Manrope']">
            © {new Date().getFullYear()} Virtual Client. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
